import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import { get, compact } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  ConsumerReviewsEntities,
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
} from 'client/data/models/consumer-reviews';
import {
  buildRecommendedTrimPath,
  EditorialReviewEntities,
  EditorialReviewModel,
  FirstContentEntity,
} from 'client/data/models/editorial-review';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { InventoryAggregateEntities } from 'client/data/models/inventory-aggregate';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { RequestModel } from 'client/data/models/request';
import { SEOModel } from 'client/data/models/seo';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { buildForSaleYearsPath, VehicleModel } from 'client/data/models/vehicle-v2';
import { VehicleGraphqlEntities } from 'client/data/models/vehicle-graphql';
import { VisitorEntities } from 'client/data/models/visitor';
import { LOOKING_FOR_MODEL_MAP } from 'site-modules/core-page/constants/looking-for-model';
import { TRIMS_FEATURES } from 'site-modules/core-page/constants/trim-features';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { OLD_REVIEW_FORMAT_LAST_YEAR } from 'site-modules/shared/constants/editorial-review';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { GLOBAL_HEADER_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { CORE_GALLERY_SIZE } from 'site-modules/core-page/utils/constants';
import {
  getAppraisalLink,
  getAppraisalLinkTextFromVehicle,
} from 'site-modules/shared/utils/appraisal-link-constructor';
import { getCustomCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getParamsFromVehicle, CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { getOverallRating } from 'site-modules/shared/utils/editorial-helper';
import { generateNewFlatUrl, generateSrpLinkMakeModel } from 'site-modules/shared/utils/srp-link-constructor';
import { AnchorNavSticky } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-sticky';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { TrimsCta } from 'site-modules/shared/components/core-page/trims/trims-cta';
import { ExcoVideoCorePage } from 'site-modules/shared/components/exco-video/exco-video-core-page';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { LeaseDealsHiddenGemsLink } from 'site-modules/shared/components/lease-deals-hidden-gems-link/lease-deals-hidden-gems-link';
import { Link } from 'site-modules/shared/components/link/link';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { PricingAd } from 'site-modules/shared/components/native-ad/pricing-ad/pricing-ad';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { StickyToBottom } from 'site-modules/shared/components/sticky-to-bottom/sticky-to-bottom';
import { ZipWithDefaultActionHandling } from 'site-modules/shared/components/zip-with-default-action-handling/zip-with-default-action-handling';
import { CoreHighlights } from 'site-modules/core-page/components/core-highlights/core-highlights';
import { CorePricingHistogram } from 'site-modules/core-page/components/core-pricing-histogram/core-pricing-histogram';
import { CoreSrp } from 'site-modules/core-page/components/core-srp/core-srp';
import { CrowdsourcedDeals } from 'site-modules/core-page/components/crowdsourced-deals/crowdsourced-deals';
import {
  EditorialHighlightsLists,
  HIGHLIGHTS_NAME,
} from 'site-modules/core-page/components/editorial-highlights-lists/editorial-highlights-lists';
import { EditorialReviewBody } from 'site-modules/core-page/components/editorial-review/editorial-review-body';
import { EvInsights } from 'site-modules/core-page/components/ev-insights/ev-insights';
import { ListPriceRange } from 'site-modules/core-page/components/new-core-intro/list-price-range';
import { LookingForModel } from 'site-modules/core-page/components/new-core-intro/looking-for-model/looking-for-model';
import { NewCoreSubnav } from 'site-modules/core-page/components/new-core-subnav/new-core-subnav';
import { OtherForSaleYears } from 'site-modules/core-page/components/other-years-core-7456/other-for-sale-years';
import { PhotoGallery } from 'site-modules/core-page/components/photo-gallery/photo-gallery';
import { PreprodMiniInventory } from 'site-modules/core-page/components/preprod-mini-inventory/preprod-mini-inventory';
import { PreprodPricingButton } from 'site-modules/core-page/components/preprod-pricing-button/preprod-pricing-button';
import { PreprodScore } from 'site-modules/core-page/components/preprod-score/preprod-score';
import { PricingModule } from 'site-modules/core-page/components/pricing-module/pricing-module';
import { RatingScorecardEmbed } from 'site-modules/core-page/components/scorecard-editorial-review/rating-scorecard/rating-scorecard-embed';
import { ScorecardEditorialReviewBody } from 'site-modules/core-page/components/scorecard-editorial-review/scorecard-editorial-review-body';
import { UsedCoreSubnav } from 'site-modules/core-page/components/used-core-subnav/used-core-subnav';
import { VideoReviews } from 'site-modules/core-page/components/video-reviews/video-reviews';
import { YearSubmodelControls } from 'site-modules/core-page/components/year-submodel-controls/year-submodel-controls';
import { VehicleInfo } from './vehicle-info/vehicle-info';

import './new-core-intro.scss';

export function NewCoreIntroUI({
  className = 'pb-3_5',
  params,
  vehicle = null,
  editorialReview = null,
  consumerRatingsCount = null,
  prevYearConsumerRatingCount = null,
  recommendedTrimSlug = null,
  firstContent = null,
  hasPreprodDealers = false,
  isPreprod = false,
  visitorLocation = {},
  isMobile = false,
  inventoryCount = null,
  hasLeadFormDealers = false,
  hasMmyForSaleLink = false,
  isInternational = false,
  isConnect = false,
  isUnsoldBuildAndPriceAd = false,
  core7007Recipe = 'ctrl',
  ads11455Ads11617Recipe = 'ctrl',
  isCore7747Enabled = false,
  isAkamaiCachePrefresh = false,
  isSearchBot = false,
  isAds12269Enabled = false,
  core7705Recipe = 'ctrl',
  coreEtrActivationRecipe = 'ctrl',
  seoExperiments = [],
  enableSeot5509PriceRange = false,
  enableSeot5678TrimSaliency = false,
}) {
  const [defaultMsrp, setDefaultMsrp] = useState(null);
  const { urlYear, noEditorialReview, isDealerlessOem, isElectric, isPluginHybrid, isUsed } = params;
  const {
    year,
    make: { slug: makeSlug, name: makeName },
    model: { slug: modelSlug, name: modelName },
    submodels: { slug: submodelSlug },
  } = vehicle;

  const vehicleName = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    trimDuplicateSubmodel: true,
    updateChevroletMakeName: true,
    disableTrademarkCharacter: true,
    usedPrefix: '',
  });

  const vehicleParams = getParamsFromVehicle(vehicle);
  const photoSize = isMobile ? CORE_GALLERY_SIZE.MOBILE.HERO : CORE_GALLERY_SIZE.DESKTOP.HERO;

  const isCrawler = isAkamaiCachePrefresh || isSearchBot;

  const hasEditorialRating = !!getOverallRating(editorialReview);
  const hasCoreHighlights = !!(
    hasEditorialRating ||
    get(consumerRatingsCount, 'ratingAggregation.averageStars') ||
    get(prevYearConsumerRatingCount, 'ratingAggregation.averageStars')
  );
  const hasHighlights = get(editorialReview, 'cons.length') || get(editorialReview, 'pros.length');
  const hasPreprodCta = !!(hasPreprodDealers || inventoryCount?.total);
  const isSeot5509PriceRangeChallenger =
    enableSeot5509PriceRange && (seoExperiments || []).includes('SEOT-5510-Price-Range');

  const reviewHash = noEditorialReview ? MENU_HASH.CONSUMER_REVIEWS : MENU_HASH.REVIEWS;
  let menuItems = [
    {
      hash: isMobile ? MENU_HASH.TRIM_SUMMARY_MOBILE : MENU_HASH.TRIM_SUMMARY_DESKTOP,
      name: 'Pricing',
      customOffset: isMobile ? undefined : GLOBAL_HEADER_OFFSET,
    },
    { hash: reviewHash, name: 'Review' },
  ];

  if (!isConnect) {
    menuItems = compact([
      ...menuItems,
      { hash: MENU_HASH.COMPETITION, name: 'Compare' },
      { hash: MENU_HASH.FEATURES, name: 'Specs' },
    ]);
  }

  const lookingForModel = get(LOOKING_FOR_MODEL_MAP, [makeSlug, modelSlug, year]);

  const showLeadForm = hasLeadFormDealers && !isDealerlessOem;
  const isReviewLink = !!get(editorialReview, 'reviewLink');
  const hasScorecardReview = isReviewLink && !!getOverallRating(editorialReview);

  const trimsFeatures = !isConnect && TRIMS_FEATURES?.[makeSlug]?.[modelSlug]?.[year];
  const isSeot5678Enabled = enableSeot5678TrimSaliency && (seoExperiments || []).includes('SEOT-5678-Trim-Saliency');
  const isCore7705ChalHeader = core7705Recipe === 'chal1' || core7705Recipe === 'chal2';

  const pricingModuleCtrl = (
    <ScrollElement id={MENU_HASH.PRICING} className="subnav-pricing px-md-1 py-2 px-0_5 bg-cool-gray-100 mx-md-0">
      <PricingModule
        params={params}
        vehicle={vehicle}
        isInternational={isInternational}
        isSeot5678Enabled={isSeot5678Enabled}
      />
    </ScrollElement>
  );
  const pricingModule = !isPreprod && (
    <Fragment>
      {isUsed ? (
        <ScrollElement tag={Container} id={MENU_HASH.FOR_SALE} className="px-0">
          <CoreSrp vehicleParams={{ makeName, modelName, makeSlug, modelSlug, year }} isMobile={isMobile} showUsurpAd />
        </ScrollElement>
      ) : (
        <Fragment>
          {trimsFeatures ? (
            <Experiment name="core-7836-trims2" showDefault>
              <Recipe name="ctrl" isDefault>
                {pricingModuleCtrl}
              </Recipe>
              <Recipe name="chal">
                <ScrollElement
                  id={MENU_HASH.PRICING}
                  className="subnav-pricing px-md-1 py-2 px-0_5 bg-cool-gray-100 mx-md-0"
                >
                  <PricingModule
                    params={params}
                    vehicle={vehicle}
                    isInternational={isInternational}
                    trimsFeatures={trimsFeatures}
                    isSeot5678Enabled={isSeot5678Enabled}
                  />
                </ScrollElement>
              </Recipe>
            </Experiment>
          ) : (
            pricingModuleCtrl
          )}
        </Fragment>
      )}
    </Fragment>
  );

  const evInsightsModule = !isPreprod && !isConnect && (isElectric || isPluginHybrid) && (
    <RatingScorecardEmbed childrenClassName="mb-1_5" isEmbed={hasScorecardReview}>
      <EvInsights
        // TODO: Remove after core-7747
        headingClassName={classnames({
          'heading-3 d-inline me-md-1': !isPreprod || isCore7747Enabled,
          'heading-4 d-inline me-md-1': isPreprod && !isCore7747Enabled,
        })}
        vehicle={vehicle}
        isMobile={isMobile}
        isPluginHybrid={isPluginHybrid}
        isInternational={isInternational}
      />
    </RatingScorecardEmbed>
  );

  const filmstripModule = (
    <MinHeightWrapper
      componentType="SPONSORED_CARS_STACKED"
      className="core-also-viewed-vehicles"
      style={isMobile ? null : { maxWidth: '744px' }}
    >
      <SponsoredCars
        trackLoading
        wrapperClassName="mt-2_5"
        showBottomDividingLine={false}
        showLargeArrows
        showStackedAds
      />
      <Experiment name="ads-12460_dfp" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal" />
      </Experiment>
    </MinHeightWrapper>
  );

  const noForSaleText = (
    <Fragment>
      No {year} {makeName} {modelName} vehicles for sale in our network near{' '}
      <ZipWithDefaultActionHandling
        buttonClassName="text-primary-darker fw-normal text-underline py-0_25"
        showDropdownIcon={false}
      />
    </Fragment>
  );

  let srpLinkDescription = inventoryCount?.total ? 'Shop vehicle inventory at dealers near you' : noForSaleText;

  if (isInternational) {
    srpLinkDescription = `Shop ${year} ${makeName} ${modelName} vehicles for sale nationwide`;
  }

  const hasForSaleLinkCore7007 = !isDealerlessOem && core7007Recipe.includes('chal');
  const hasForSaleLink = hasMmyForSaleLink || hasForSaleLinkCore7007;
  let secondSrpLinkUrl;
  switch (true) {
    case !isDealerlessOem && core7007Recipe.includes('chal'): {
      secondSrpLinkUrl = generateNewFlatUrl({ make: makeSlug, model: modelSlug });
      break;
    }
    case hasForSaleLink: {
      secondSrpLinkUrl = getCustomCoreUrl({ makeSlug, modelSlug, year }, 'for-sale/');
      break;
    }
    default: {
      secondSrpLinkUrl = generateSrpLinkMakeModel(makeSlug, modelSlug);
    }
  }

  const core7007LabelMap = {
    ctrl: 'See All for Sale',
    chal1: `Find your new ${makeName} ${modelName}`,
    chal2: `See ${makeName} ${modelName} Inventory`,
  };

  const secondSrpLinkProps = {
    to: secondSrpLinkUrl,
    'data-tracking-id': hasForSaleLink ? 'floating_srp_link' : 'floating_used_srp_link',
    label: hasForSaleLinkCore7007 ? core7007LabelMap[core7007Recipe] : 'See All for Sale',
    creativeId: 'srp-link-under-site-incentives',
    className: 'px-1 px-md-1_5 py-0_75 py-md-1 bg-white rounded-8',
    descriptionClassName: 'mt-0_25 medium fst-italic',
    iconClassName: 'icon-price-tags2',
    iconContainerClassName: 'me-0_75 me-md-1_5 size-16 text-blue-50',
    iconStyles: isMobile ? { height: '40px', width: '40px' } : { height: '48px', width: '48px' },
    description: isMobile ? null : srpLinkDescription,
  };

  const highlightsListFallback = (
    <EditorialHighlightsLists
      editorialReview={editorialReview}
      firstContent={firstContent}
      params={params}
      headingClassName={isCore7747Enabled || !isPreprod ? 'heading-3 mb-0_5' : undefined}
      className={classnames({
        'mb-2': isMobile || isPreprod,
        'pb-0_5': !isMobile && !isPreprod,
      })}
      isPreprod={isPreprod}
      sectionsToRender={hasHighlights ? [HIGHLIGHTS_NAME.PROS, HIGHLIGHTS_NAME.CONS] : [HIGHLIGHTS_NAME.WHAT_TO_EXPECT]}
    />
  );

  const introOtherYearsPills = (
    <Fragment>
      {!isPreprod && (
        <Fragment>
          <LeaseDealsHiddenGemsLink
            make={makeName}
            model={modelName}
            year={year}
            className="px-1 px-md-1_5 py-0_75 py-md-1 bg-white rounded-8 mb-1"
            isMobile={isMobile}
            parentCreativeId="edmunds_highlights"
          />

          <ShoppingLink {...secondSrpLinkProps} />
          {isMobile && !inventoryCount?.total && !isInternational && (
            <div className="medium fst-italic mt-0_75">{noForSaleText}</div>
          )}
        </Fragment>
      )}

      {!isCrawler && (
        <Experiment name="QB-43-New-Core-V3-Signup" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal-1">
            <CrowdsourcedDeals chal="chal-1" isMobile={isMobile} vehicle={vehicle} defaultMsrp={defaultMsrp} />
          </Recipe>
          <Recipe name="chal-2">
            <CrowdsourcedDeals chal="chal-2" isMobile={isMobile} vehicle={vehicle} defaultMsrp={defaultMsrp} />
          </Recipe>
          <Recipe name="chal-3">
            <CrowdsourcedDeals chal="chal-3" isMobile={isMobile} vehicle={vehicle} defaultMsrp={defaultMsrp} />
          </Recipe>
          <Recipe name="chal-4" />
        </Experiment>
      )}

      <OtherForSaleYears
        className="mt-2"
        heading={`${makeName} ${modelName} for Sale`}
        headingClassName="mb-0_75"
        vehicle={vehicle}
        isMobile={isMobile}
      />
    </Fragment>
  );

  const shortVideosModule = isAds12269Enabled ? (
    <ExcoVideoCorePage id="genesis-video-shorts" />
  ) : (
    <VideoReviews
      params={params}
      className={classnames({ 'mb-3_5': !editorialReview, 'mt-2 mb-2': editorialReview })}
      // TODO: Remove after core-7747
      headingClassName={classnames({
        'heading-3 mb-0_5': !isPreprod || isCore7747Enabled,
        'heading-4 mb-0_5': isPreprod && !isCore7747Enabled,
      })}
      vehicleName={`${makeName} ${modelName}`}
      creativeId="video-carousel"
    />
  );

  const incentiveAdsDesktopFragment = (
    <IncentivesAd
      wrapperClass="mt-1_5 mt-md-1_5 mb-1"
      cardWrapper={classnames('rounded-12', {
        'bg-cool-gray-90': ads11455Ads11617Recipe === 'ctrl',
      })}
      horizontalLayout
      showOnlySiteServed
      ads11455Ads11617Recipe={ads11455Ads11617Recipe}
    />
  );

  const buildPriceAdModule = (
    <BuildPriceAd mobile={isMobile} wrapperClass="d-flex justify-content-center mb-2" includeMrectAdCall />
  );

  const vehicleInfoFragment = isUsed ? (
    <ListPriceRange
      className={classnames({
        'mb-0_75 my-md-0 text-white pt-0_5 pb-md-0_5 pe-md-1': !core7705Recipe.includes('chal'),
        'text-white': !(core7705Recipe === 'chal2' && !isMobile),
      })}
      valueClassName="d-inline-block fw-bold size-20"
      params={params}
      submodelSlug={submodelSlug}
      isUnknownZip={isInternational}
    />
  ) : (
    !isSeot5509PriceRangeChallenger && (
      <VehicleInfo
        vehicle={vehicle}
        firstContent={firstContent}
        isPreprod={isPreprod}
        isMobile={isMobile}
        className={classnames({
          'mb-0_75 my-md-0': !core7705Recipe.includes('chal'),
          'text-white': !(core7705Recipe === 'chal2' && !isMobile),
        })}
        priceRangeClassName={core7705Recipe.includes('chal') ? null : undefined}
      />
    )
  );

  const core7705ChalFragment = (
    <div
      className={classnames('core-7705 d-flex flex-column gap-0_5', {
        'mb-1': core7705Recipe === 'chal1' || isMobile,
        'mt-1_5': core7705Recipe === 'chal2' && !isMobile,
      })}
    >
      <h1
        className={classnames('intro-title heading-2 mb-0', {
          'text-white': core7705Recipe === 'chal1' || isMobile,
        })}
      >
        {vehicleName}
      </h1>
      {vehicleInfoFragment}
      {isUsed ? (
        <UsedCoreSubnav
          linkClassName={classnames({ 'text-white': core7705Recipe === 'chal1' || isMobile })}
          params={params}
          vehicle={vehicle}
          isMobile={isMobile}
          isMainContentWidth={core7705Recipe === 'chal2' && !isMobile}
        />
      ) : (
        <NewCoreSubnav
          linkClassName={classnames({ 'text-white': core7705Recipe === 'chal1' || isMobile })}
          params={params}
          vehicle={vehicle}
          isMobile={isMobile}
          isMainContentWidth={core7705Recipe === 'chal2' && !isMobile}
        />
      )}
    </div>
  );

  return (
    <Container className={classnames('new-core-intro', className)}>
      {!core7705Recipe.includes('chal') && (
        <div className="d-flex flex-wrap align-items-start mb-md-0_5">
          <h1 className="intro-title heading-2 text-white mb-0_5 me-md-1_5">{vehicleName}</h1>
          <LookingForModel model={lookingForModel} />
        </div>
      )}
      {!core7705Recipe.includes('chal') && (!isConnect || !isMobile) && (
        <Row
          className={classnames('intro-header align-items-end justify-content-between mb-0_5 mb-md-1_5', {
            'border-0': isPreprod && isMobile,
          })}
        >
          <Col xs={12} md={6} lg={7} className="px-0_5">
            {vehicleInfoFragment}
          </Col>
          {/* Empty Col can be rendered for critical CSS (CLS) */}
          <Col xs={12} md={6} lg={5} className="px-0_5">
            {!isPreprod && !isUsed && (
              <AnchorNavSticky
                navList={menuItems}
                title={null}
                stickyTitle={vehicleName}
                hasSeparator={false}
                containerClassName={null}
                titleClassName="heading-4 py-0_75 hidden-md-down"
                className="intro-navigation"
                stickyClassName="justify-content-between pt-1 pt-md-0"
                listClassName={classnames('d-flex align-items-end mx-0 size-16', {
                  'justify-content-between': !isConnect,
                  'justify-content-md-end': isConnect,
                })}
                stickyListClassName={classnames('d-flex align-items-end mx-0 medium', {
                  'justify-content-between': !isConnect,
                  'justify-content-md-end': isConnect,
                })}
                anchorNavContentClassName="w-100"
                stickyAnchorNavContentClassName="col-12 col-md-6 col-lg-5 align-self-end px-0 ps-md-0_5"
                navWrapperClassName={null}
                itemClassName="px-0_75 px-xl-1"
                stickyItemClassName="px-0_75 px-xl-1"
                linkClassName="pt-md-0 text-white px-md-0_25 py-0_5"
                stickyLinkClassName="pt-md-0 py-0_5"
                linkActiveClassName="link-active fw-bold"
                stickyLinkActiveClassName="link-active text-primary-darker"
                creativeId="edm-entry-sub-nav"
                isMobile={isMobile}
                linkTrackingId="view_content_non_docked"
                stickyLinkTrackingId="view_content_docked"
                useNoFollow={false}
              />
            )}
          </Col>
        </Row>
      )}
      {(core7705Recipe === 'chal1' || (isMobile && core7705Recipe === 'chal2')) && core7705ChalFragment}
      <div
        className={classnames('intro-content', {
          'has-other': !hasScorecardReview,
          'has-years': isPreprod,
          'new-core-used': isUsed,
        })}
      >
        <ScrollElement className="intro-gallery overview-container" id={MENU_HASH.OVERVIEW}>
          <PhotoGallery
            params={params}
            isMobile={isMobile}
            vehicle={vehicle}
            editorialReview={editorialReview}
            isPreprod={isPreprod}
            photoSize={photoSize}
            isConnect={isConnect}
            hideGalleryRow={isMobile || isConnect}
            isCore6981Test={!isPreprod && !isConnect}
            coreEtrActivationRecipe={coreEtrActivationRecipe}
          />
          {core7705Recipe === 'chal2' && !isMobile && core7705ChalFragment}
          <div className="core-highlights-section bg-cool-gray-90 px-1 py-1 rounded-12 mt-1 mt-md-1_5">
            {hasCoreHighlights && (
              <CoreHighlights
                className="mb-1"
                params={params}
                submodelSlug={submodelSlug}
                vehicle={vehicle}
                editorialReview={editorialReview}
                isMobile={isMobile}
                noCardsRow={isPreprod || isConnect}
                isUsed={isUsed}
              />
            )}
            {!isMobile && !isPreprod && (
              <Fragment>
                {!hasCoreHighlights && highlightsListFallback}
                {incentiveAdsDesktopFragment}
                {introOtherYearsPills}
              </Fragment>
            )}
          </div>
        </ScrollElement>
        <ScrollElement id={MENU_HASH.TRIM_SUMMARY_DESKTOP} className="intro-trims d-flex flex-column">
          <StickyToBottom enabled={!isMobile} offsetSelector=".intro-header" additionalOffset={16}>
            {({ onHeightUpdate }) => (
              <Fragment>
                {isPreprod ? (
                  <div className="trim-summary-section rounded-12 px-1 pb-1 pt-1 mt-1 mt-md-1_5 mt-lg-0 bg-cool-gray-90">
                    {!hasEditorialRating && (
                      <PreprodScore
                        className="bg-white rounded-8 mb-1 px-1 px-lg-0_5 px-xl-1 py-1"
                        firstImpressionScore={editorialReview?.firstContent?.firstImpressionScore}
                      />
                    )}
                    <ScrollElement
                      id={MENU_HASH.TRIM_SUMMARY_MOBILE}
                      tag={Card}
                      className="intro-trims-content px-1 px-lg-0_5 px-xl-1 py-1 m-lg-0 border-0 rounded-8"
                      data-tracking-parent="edm-entry-model-summary"
                    >
                      <YearSubmodelControls params={params} vehicle={vehicle} isPreprod />
                      {((!(hasHighlights && hasPreprodCta) && !isMobile) || (isMobile && !hasCoreHighlights)) &&
                        highlightsListFallback}
                      <PreprodPricingButton
                        hasSrpLink={!!inventoryCount?.total}
                        vehicle={vehicle}
                        isMobile={isMobile}
                        copyClassName="mb-1 text-gray-darker"
                        btnClassName="w-100 py-0_75 px-1"
                      />
                      {isMobile && (
                        <Fragment>
                          <hr className="mt-1 mb-0_75 w-100" style={{ borderColor: '#64748B' }} />
                          <IncentivesAd wrapperClass="px-lg-0" mobile={isMobile} showOnlySiteServed />
                        </Fragment>
                      )}
                    </ScrollElement>
                  </div>
                ) : (
                  <div className="trim-summary-section modular rounded-12 bg-cool-gray-90 pb-1 pt-1 px-1 mt-1 mt-md-1_5 mt-lg-0">
                    {isMobile && !hasCoreHighlights && highlightsListFallback}
                    <h2 className="heading-3 mb-1_5">{isSeot5509PriceRangeChallenger ? 'Price' : 'Pricing'}</h2>
                    <ScrollElement
                      id={MENU_HASH.TRIM_SUMMARY_MOBILE}
                      tag={Card}
                      className="intro-trims-content px-1 px-lg-0_5 px-xl-1 py-1 m-lg-0 border-0 rounded-8"
                      data-tracking-parent="edm-entry-model-summary"
                    >
                      <YearSubmodelControls params={params} vehicle={vehicle} />
                      {isUsed ? (
                        <CorePricingHistogram />
                      ) : (
                        <TrimsCta
                          params={vehicleParams}
                          vehicle={vehicle}
                          recommendedTrimSlug={recommendedTrimSlug}
                          visitorLocation={visitorLocation}
                          showLeadForm={showLeadForm}
                          isDealerlessOem={isDealerlessOem}
                          isMobile={isMobile}
                          setDefaultMsrp={setDefaultMsrp}
                          isCore7026Test
                        />
                      )}

                      {!isConnect && isMobile && (
                        <Fragment>
                          <hr className="w-100 mt-2 mb-0_5" />
                          <IncentivesAd
                            mobile
                            showOnlySiteServed
                            plainLayout
                            ads11455Ads11617Recipe={ads11455Ads11617Recipe}
                          />
                        </Fragment>
                      )}
                    </ScrollElement>
                    {isUsed && (
                      <Button
                        tag={Link}
                        to={getAppraisalLink({ make: makeSlug, model: modelSlug, year })}
                        data-tracking-id="tool_select_appraise_your_car"
                        size="lg"
                        color="outline-blue-50"
                        className="cta-button rounded-8 w-100 py-0_75 px-1 text-transform-none btn-bg-white mt-1_5"
                      >
                        {getAppraisalLinkTextFromVehicle(vehicle)}
                      </Button>
                    )}
                  </div>
                )}
                {!isMobile && !isCrawler && (
                  <div style={{ maxWidth: '372px' }} className="mx-auto my-1 my-md-0">
                    <BuildPriceAd
                      wrapperClass="d-flex justify-content-center"
                      showOnlyNarrowView
                      position={'1'}
                      slotRenderEndListener={onHeightUpdate}
                      hideJelly
                    />
                  </div>
                )}
              </Fragment>
            )}
          </StickyToBottom>
          {!isMobile && isUnsoldBuildAndPriceAd && (
            <MrectNative
              position={'0'}
              wiredBreakpoints={{ lg: true, xl: true }}
              useMinHeight
              wrapperClass="mt-1_5 hidden-lg-down"
            />
          )}
        </ScrollElement>

        {(isMobile || isPreprod) && (
          <div className="intro-other-years-pills mt-2 mt-md-1 mt-lg-0 mb-2 mb-lg-0">
            <div
              className={classnames({
                'with-srp-link-container bg-cool-gray-90 rounded-12 mx-0 p-1': !isPreprod,
              })}
            >
              {introOtherYearsPills}
            </div>
            {!isMobile && isPreprod && <IncentivesAd wrapperClass="mt-2" horizontalLayout showOnlySiteServed />}
          </div>
        )}

        {!!(editorialReview || isPreprod || isUsed) && (
          <ScrollElement id={MENU_HASH.REVIEWS} className="intro-editorial">
            {!editorialReview && shortVideosModule}
            {isUsed && !isCore7705ChalHeader && (
              <UsedCoreSubnav className="mb-1_5" params={params} vehicle={vehicle} isMobile={isMobile} />
            )}
            {!!editorialReview &&
              (year > OLD_REVIEW_FORMAT_LAST_YEAR ? (
                <ScorecardEditorialReviewBody
                  params={params}
                  vehicle={vehicle}
                  isUsed={isUsed}
                  hasRouteYearParam={!!urlYear}
                  isPreprod={isPreprod}
                  showHighlights
                  hasPreprodCta={hasPreprodCta}
                  embeddedModules={{
                    pricingModule,
                    evInsightsModule,
                    shortVideosModule,
                    filmstripModule,
                  }}
                  isExpandedScorecard
                  hasCoreHighlights={hasCoreHighlights}
                  hasTrimsLink={!isPreprod}
                  showTrimsReview={isConnect}
                  isCrawler={isCrawler}
                  defaultMsrp={defaultMsrp}
                  isCore7747Test={isPreprod}
                  isCore7747Enabled={isCore7747Enabled}
                />
              ) : (
                <EditorialReviewBody
                  vehicle={vehicle}
                  params={params}
                  hasRouteYearParam={!!urlYear}
                  isUsed
                  showHighlights
                  showBodyScorecard
                  expandReview
                  isReordered
                  embeddedModules={{ coreSrpModule: pricingModule, evInsightsModule, buildPriceAdModule }}
                />
              ))}
            {isPreprod && (
              <Fragment>
                <Experiment name="ads-10779" showDefault>
                  <Recipe name="ctrl" isDefault>
                    <PricingAd wrapperClass="mt-1_5 mb-1_5" mobile={isMobile} showHorizontalLayout showOnlySiteServed />
                  </Recipe>
                  <Recipe name="chal1" />
                  <Recipe name="chal2" />
                  <Recipe name="chal3" />
                </Experiment>
                <PreprodMiniInventory
                  params={params}
                  modelFamilyIds={vehicle?.modelFamilyIds}
                  vehicleParams={{ makeName, modelName, makeSlug, modelSlug, year: year - 1 }}
                  isMobile={isMobile}
                />
              </Fragment>
            )}
          </ScrollElement>
        )}

        {!hasScorecardReview && (
          <Fragment>
            {!isCrawler && (
              <Experiment name="QB-43-New-Core-V3-Signup" showDefault>
                <Recipe name="ctrl" isDefault />
                <Recipe name="chal-1" />
                <Recipe name="chal-2" />
                <Recipe name="chal-3" />
                <Recipe name="chal-4">
                  <div className="p-1 bg-cool-gray-90 rounded-12">
                    <CrowdsourcedDeals chal="chal-4" isMobile={isMobile} vehicle={vehicle} defaultMsrp={defaultMsrp} />
                  </div>
                </Recipe>
              </Experiment>
            )}
            <div className="intro-other">
              {!isReviewLink && (
                <Fragment>
                  {evInsightsModule}
                  {pricingModule}
                  {filmstripModule}
                </Fragment>
              )}
            </div>
          </Fragment>
        )}
      </div>
    </Container>
  );
}

NewCoreIntroUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleGraphqlEntities.Vehicle.isRequired,
  className: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  isMobile: PropTypes.bool,
  editorialReview: EditorialReviewEntities.EditorialReview,
  consumerRatingsCount: ConsumerReviewsEntities.ConsumerReviewsRatingsCount,
  prevYearConsumerRatingCount: ConsumerReviewsEntities.ConsumerReviewsRatingsCount,
  firstContent: FirstContentEntity,
  recommendedTrimSlug: PropTypes.string,
  inventoryCount: InventoryAggregateEntities.InventoryCountByType,
  hasLeadFormDealers: PropTypes.bool,
  hasPreprodDealers: PropTypes.bool,
  hasMmyForSaleLink: PropTypes.bool,
  isPreprod: PropTypes.bool,
  isInternational: PropTypes.bool,
  isConnect: PropTypes.bool,
  isUnsoldBuildAndPriceAd: PropTypes.bool,
  core7007Recipe: PropTypes.string,
  ads11455Ads11617Recipe: PropTypes.string,
  isCore7747Enabled: PropTypes.bool,
  isAkamaiCachePrefresh: PropTypes.bool,
  isSearchBot: PropTypes.bool,
  core7705Recipe: PropTypes.string,
  isAds12269Enabled: PropTypes.bool,
  coreEtrActivationRecipe: PropTypes.string,
  seoExperiments: PropTypes.arrayOf(PropTypes.string),
  enableSeot5509PriceRange: PropTypes.bool,
  enableSeot5678TrimSaliency: PropTypes.bool,
};

export const NewCoreIntro = connectToModel(NewCoreIntroUI, {
  seoExperiments: bindToPath('seoExperiments', SEOModel),
  enableSeot5509PriceRange: bindToPath('enable-seot-5509-price-range', FeatureFlagModel),
  enableSeot5678TrimSaliency: bindToPath('enable-seot-5678-trim-saliency', FeatureFlagModel),
  hasLeadFormDealers: bindToPath(
    ({ vehicle, isPreprod, params }) =>
      !params.isUsed && !isPreprod && buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
    InventoryCoreConfigurationsModel,
    trims => !!get(trims, '[0].hasLeadFormDealers')
  ),
  recommendedTrimSlug: bindToPath(
    ({ vehicle }) => buildRecommendedTrimPath(getParamsFromVehicle(vehicle)),
    EditorialReviewModel
  ),
  hasPreprodDealers: bindToPath(
    ({ vehicle, isPreprod }) =>
      isPreprod &&
      getUnifiedLeadFormDealerCountV2Path({
        makeSlug: vehicle.make.slug,
        modelSlug: vehicle.model.slug,
        strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_ROI.strategy,
      }),
    UnifiedLeadFormModel,
    dealerCount => !!dealerCount
  ),
  hasMmyForSaleLink: bindToPath(
    ({ vehicle, core7007Recipe }) =>
      core7007Recipe === 'ctrl' &&
      buildForSaleYearsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
    VehicleModel,
    (forSaleYears, { vehicle: { year } }) => forSaleYears.includes(parseInt(year, 10))
  ),
  consumerRatingsCount: bindToPath(
    ({ params, isPreprod, isConnect }) =>
      !isPreprod && !isConnect && ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath(params),
    ConsumerReviewsModel
  ),
  prevYearConsumerRatingCount: bindToPath(
    ({ params, isPreprod, isConnect }) =>
      !isPreprod &&
      !isConnect &&
      ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({ ...params, year: params.year - 1 }),
    ConsumerReviewsModel
  ),
  isUnsoldBuildAndPriceAd: bindToPath('isUnsoldBuildAndPriceAd', NativeAdsCreativeConfigModel),
  isSearchBot: bindToPath('isSearchBot', RequestModel),
  isAkamaiCachePrefresh: bindToPath('isAkamaiCachePrefresh', RequestModel),
});
